import PayPalCheckoutButtons from "@features/payment/components/PayPalCheckoutButtons";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

function poundsToPence(pounds: number): number {
  return Math.round(pounds * 100);
}

export function PayPalSandboxHarnessPage() {
  "use no memo";
  const amountPoundsRef = useRef(1);
  const [amountPounds, setAmountPounds] = useState(1);
  const [lines, setLines] = useState<string[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const amountFieldId = useId();

  const pushLine = useCallback((text: string) => {
    setLines((prev) => [...prev.slice(-80), `${new Date().toISOString()} ${text}`]);
  }, []);

  const metadata = useMemo(
    () => ({
      source: "paypal_sandbox_harness",
    }),
    []
  );

  useEffect(() => {
    amountPoundsRef.current = amountPounds;
  }, [amountPounds]);

  const raw = import.meta.env.VITE_PAYPAL_CLIENT_ID;
  const clientId = typeof raw === "string" ? raw.trim() : "";

  return (
    <div
      className="min-h-screen bg-zinc-950 p-6 text-zinc-100"
      style={{ minHeight: "100vh", backgroundColor: "#09090b", color: "#fafafa" }}
    >
      <div className="mx-auto max-w-2xl space-y-4">
        <h1 className="text-xl font-semibold tracking-tight">PayPal sandbox harness</h1>
        <p className="rounded-md border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-300">
          Same checkout stack as production: <code className="text-zinc-400">PayPalCheckoutButtons</code> →{" "}
          <code className="text-zinc-400">paypalCheckoutCreateIntent</code> /{" "}
          <code className="text-zinc-400">paypalCheckoutChargeResult</code> →{" "}
          <code className="text-zinc-400">PaymentProvider</code> via{" "}
          <code className="text-zinc-400">PAYMENT_PROVIDER</code>. Use{" "}
          <a
            className="font-medium text-pink-400 underline hover:text-pink-300"
            href="https://developer.paypal.com/tools/sandbox/card-testing/"
            rel="noopener noreferrer"
            target="_blank"
          >
            sandbox test accounts and cards
          </a>
          .
        </p>
        <p className="text-sm text-zinc-400" style={{ color: "#a1a1aa" }}>
          <code className="text-zinc-300">yarn dev</code> →{" "}
          <code className="text-zinc-300">/api/payments/paypal/*</code> (Vite) →{" "}
          <code className="text-zinc-300">createPaymentProviderFromEnv</code> →{" "}
          <code className="text-zinc-300">/app/paypal-sandbox</code>
        </p>
        {banner ? (
          <p className="rounded-md border border-amber-800 bg-amber-950/50 px-3 py-2 text-sm text-amber-100">
            {banner}
          </p>
        ) : null}
        {!clientId ? (
          <p className="rounded-md border border-amber-800 bg-amber-950/50 px-3 py-2 text-sm text-amber-100">
            Add <code className="text-amber-200">VITE_PAYPAL_CLIENT_ID</code> to{" "}
            <code className="text-amber-200">.env</code> (public Client ID, same REST app as{" "}
            <code className="text-amber-200">PAYPAL_API_KEY</code>). Restart{" "}
            <code className="text-amber-200">yarn dev</code>.
          </p>
        ) : null}

        <label className="block text-sm font-medium text-zinc-300" htmlFor={amountFieldId}>
          Amount (GBP)
        </label>
        <input
          className="w-full max-w-xs rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-zinc-100"
          data-testid="paypal-harness-amount-gbp"
          id={amountFieldId}
          min={0.01}
          onChange={(e) => setAmountPounds(Number.parseFloat(e.target.value) || 0)}
          step={0.01}
          type="number"
          value={amountPounds}
        />

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-zinc-200">Checkout</h2>
          {clientId ? (
            <PayPalCheckoutButtons
              clientId={clientId}
              containerClassName="min-h-[52px] rounded-md border border-zinc-800 bg-zinc-900/50 p-2"
              currency="gbp"
              data-testid="paypal-harness-buttons"
              getAmountCents={() => poundsToPence(amountPoundsRef.current)}
              metadata={metadata}
              onErrorBanner={setBanner}
              onLog={pushLine}
            />
          ) : null}
        </section>

        <div>
          <h2 className="mb-1 text-sm font-medium text-zinc-400">Log</h2>
          <pre
            className="max-h-64 overflow-auto rounded-md border border-zinc-800 bg-black/40 p-3 text-xs text-zinc-300"
            data-testid="paypal-harness-log"
          >
            {lines.length === 0 ? "No events yet." : lines.join("\n")}
          </pre>
        </div>
      </div>
    </div>
  );
}
